<template>
  <div class="animated fadeIn">
    <b-form-group label="Sample ID" label-cols="2">
      <b-form-input
        type="text"
        v-model="identifier"
        placeholder="Sample ID"
        invalid-feedback="Required"
        :state="!$v.identifier.$invalid"
      />
    </b-form-group>

    <b-form-group label="Depth" label-cols="2">
      <b-row>
        <b-col class="pr-0">
          <b-input-group append='"'>
            <b-form-input
              type="number"
              v-model.number="depth_start"
              placeholder="Start"
              invalid-feedback="Required"
              :state="!$v.depth_start.$invalid"
            />
          </b-input-group>
        </b-col>

        <b-col class="pr-0">
          <b-input-group append='"'>
            <b-form-input
              type="number"
              v-model.number="depth_end"
              placeholder="End"
              invalid-feedback="Required"
              :state="!$v.depth_end.$invalid"
            />
          </b-input-group>
        </b-col>

        <b-col>
          <b-input-group append='"'>
            <b-form-input
              type="number"
              :value="sample_soil.depth_total"
              placeholder="Total"
              disabled
            />
          </b-input-group>
        </b-col>
      </b-row>
    </b-form-group>

    <b-form-group label="Add On(s)" label-cols="2">
      <b-button
        v-b-toggle="`addOns-${sample.test_package_sample_id}${submissionId}`"
        :variant="add_ons.length ? 'primary' : 'dark'"
        block
        >Options</b-button
      >
      <b-collapse
        :id="`addOns-${sample.test_package_sample_id}${submissionId}`"
        class="mt-2"
      >
        <custom-form-checkbox-group
          v-if="filteredAddOns.length"
          v-model="add_ons"
          :lg="2"
          :md="2"
          :options="
            filteredAddOns.map((item) => ({
              text: item.name,
              value: item.id,
              icon: 'cube',
            }))
          "
        />
      </b-collapse>
    </b-form-group>

    <b-form-group label="Analytes" label-cols="2" class="mb-0">
      {{ sampleAnalytes }}
    </b-form-group>
  </div>
</template>

<script>
import CustomFormCheckboxGroup from "@/components/shared/form/CustomFormCheckboxGroup";
import ToastNotifications from "@/components/shared/mixins/ToastNotifications";
import { mapActions, mapGetters } from "vuex";
import { required } from "vuelidate/lib/validators";
import { validationMixin } from "vuelidate/src";

export default {
  name: "ProjectSoilSampleFormGroup",
  components: { CustomFormCheckboxGroup },
  mixins: [validationMixin, ToastNotifications],
  props: {
    submissionId: {
      type: String,
      default: "",
    },
    sampleId: {
      type: String,
      default: "",
    },
    zone_id: {
      type: String,
      default: "",
    },
    addOns: {
      type: Array,
      default: () => [],
    },
    testPackage: {
      type: Object,
      default: () => (null),
    },
    allTestPackages: {
      type: Array,
      default: () => [],
    }
  },
  computed: {
    ...mapGetters({
      submission: "projectFormStepTwoSubmission",
    }),
    current_submission() {
      return this.submission(this.submissionId);
    },
    zone() {
      return this.current_submission.zones.find(
        (item) => item.zone_id === this.zone_id
      );
    },
    sample() {
      return this.zone.samples.find(
        (sample) => sample.test_package_sample_id === this.sampleId
      );
    },
    sample_soil() {
      return this.sample.sample_soil;
    },
    sample_prefix() {
      return this.zone.identifier && this.zone.identifier.length > 4
        ? `${this.zone.identifier.substr(0, 4)}...`
        : this.zone.identifier;
    },
    identifier: {
      get() {
        return this.sample.identifier;
      },
      set(value) {
        this.handleSampleUpdate({
          field: "identifier",
          value,
        });
      },
    },
    depth_start: {
      get() {
        return this.sample_soil.depth_start;
      },
      set(value) {
        this.handleSampleUpdate({
          field: "sample_soil",
          value: {
            ...this.sample_soil,
            depth_start: value,
            depth_total: this.depth_end - value,
          },
        });
      },
    },
    depth_end: {
      get() {
        return this.sample_soil.depth_end;
      },
      set(value) {
        this.handleSampleUpdate({
          field: "sample_soil",
          value: {
            ...this.sample_soil,
            depth_end: value,
            depth_total: value - this.depth_start,
          },
        });
      },
    },
    add_ons: {
      get() {
        return this.sample.add_ons;
      },
      set(value) {
        this.handleSampleUpdate({
          field: "add_ons",
          value,
        });
      },
    },
    test_package_samples () {
      return this.testPackage && this.testPackage.test_package_samples || []
    },
    sampleAnalytes () {
      let test_package_sample = this.test_package_samples && this.test_package_samples.find(item => item.id === this.sample.test_package_sample_id)
      let test_package_analytes = test_package_sample && test_package_sample.test_package_analytes

      if (!test_package_analytes) return ''

      return test_package_analytes.map(({ analyte }) => {
        return analyte.abbrev || ""
      }).sort((a, b) => a.localeCompare(b)).join(', ')
    },
    filteredAddOns() {
      let test_package_sample = this.test_package_samples && this.test_package_samples.find(item => item.id === this.sample.test_package_sample_id)
      let test_package_analytes = test_package_sample && test_package_sample.test_package_analytes
      let test_package_analytes_ids = new Set(test_package_analytes && test_package_analytes.map(({ analyte }) => analyte.id))

      return this.allTestPackages
        .filter((i) => i.cost_add_on)
        .filter((testPackage) => {
          return testPackage.test_package_samples.some(sample => {
            return sample.test_package_analytes.some(({ analyte }) => {
              return !test_package_analytes_ids.has(analyte.id)
            })
          })
        })
    }
  },
  validations: {
    identifier: { required },
    depth_start: { required },
    depth_end: { required },
  },
  methods: {
    ...mapActions(["UPDATE_SUBMISSION_SAMPLE", "UPDATE_SUBMISSION"]),
    handleSampleUpdate({ value, field }) {
      this.sample[field] = value;
      this.UPDATE_SUBMISSION({
        id: this.submissionId,
        value: this.current_submission.zones,
        name: "zones",
      });
    }
  },
};
</script>
